import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/SimpleToast";
import { Loader2, LogIn, Shield, ArrowLeft } from "lucide-react";
import axios from "axios";

const API_URL = '/api';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // First attempt without TOTP
      const response = await axios.post(`${API_URL}/auth/login`, { 
        email, 
        password,
        totp_code: totpRequired ? totpCode : null
      });
      
      const data = response.data;
      
      // Check if TOTP is required
      if (data.totp_required) {
        setTotpRequired(true);
        setLoading(false);
        toast.info("Bitte geben Sie Ihren 2FA-Code ein");
        return;
      }
      
      // Login successful (backend set the secure HttpOnly auth cookie)
      if (data.access_token) {
        // If 2FA setup is required (new user / no 2FA setup yet), redirect to setup
        if (data.totp_setup_required) {
          toast.info("2FA muss eingerichtet werden");
          navigate("/setup-2fa");
          window.location.reload();
          return;
        }
        
        toast.success("Erfolgreich angemeldet");
        navigate("/");
        window.location.reload(); // Refresh to update auth state
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Anmeldung fehlgeschlagen");
      if (totpRequired) {
        setTotpCode(""); // Clear code on error
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setTotpRequired(false);
    setTotpCode("");
    setPassword("");
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.95)), url('https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwYWJzdHJhY3QlMjBibHVlfGVufDB8fHx8MTc3MDA1NTkwM3ww&ixlib=rb-4.1.0&q=85')`
      }}
    >
      <div className="w-full max-w-md animate-fade-in">
        {/* Firmenlogo */}
        <div className="text-center mb-8">
          <img 
            src="/assets/rnetwork-logo.webp" 
            alt="R-Network GmbH" 
            className="h-16 mx-auto mb-4 bg-white rounded-lg px-4 py-2"
          />
          <h1 className="text-3xl font-bold text-white">CRM-System</h1>
          <p className="text-slate-400 mt-2">Internes Service-Management</p>
        </div>

        <Card className="border-border/50 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl flex items-center justify-center gap-2">
              {totpRequired && <Shield className="w-5 h-5 text-primary" />}
              {totpRequired ? "2-Faktor-Authentifizierung" : "Anmelden"}
            </CardTitle>
            <CardDescription>
              {totpRequired 
                ? "Geben Sie den 6-stelligen Code aus Google Authenticator ein" 
                : "Melden Sie sich mit Ihren Zugangsdaten an"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {!totpRequired ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="login-email">E-Mail</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="name@r-network.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Passwort</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center py-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <Shield className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="totp-code">Authentifizierungscode</Label>
                    <Input
                      id="totp-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                      required
                      autoFocus
                      data-testid="totp-code-input"
                    />
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Code läuft alle 30 Sekunden ab
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                {totpRequired && (
                  <Button 
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleBack}
                    data-testid="totp-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                )}
                <Button 
                  type="submit" 
                  className={totpRequired ? "flex-1" : "w-full h-11"} 
                  disabled={loading || (totpRequired && totpCode.length !== 6)}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : totpRequired ? (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Verifizieren
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      Anmelden
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          © 2026 R-Network GmbH - Nur für internen Gebrauch
        </p>
      </div>
    </div>
  );
}
